import React, { useEffect, useRef, useState, type RefObject } from "react";
import { Grid, IconButton } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { useStyles } from "./style";
import { ttfAction } from "../../create-loan/ttf-review/constants";
import { CircularProgressWithLabel } from "../../create-loan/ttf-review/document-review/CircularProgressWithLabel";
import { TTFReviewCacheProvider } from "../../browser-cache/local-forage/TTFReviewCacheProvider";
import {
  getPresignedURL,
  getPresignedURLForDrawDocs
} from "../../network/apiService";
import { InfoOnlyTooltip } from "../Tooltip";
import { RefreshIcon } from "../../create-loan/Icons";
import { URLParamsType } from "../constants";
import { getConfig } from "../../config/config";
import { findMinMax } from "../../create-loan/ttf-review/ttfHelperFunctions";
import { getPdfDocumentLoadParamsForUrl } from "./pdfDocumentParams";
import { PDFJS_DIST_FALLBACK_VERSION } from "./pdfjsDistFallbackVersion";
import { configurePdfJsWorker } from "./pdfjsWorker";
import { PDFViewerSidebar, PdfSidebarPanel } from "./PDFViewerSidebar";
import { PDFViewerToolbar } from "./PDFViewerToolbar";
import { findTextInDocument, PdfSearchMatch } from "./pdfSearch";
import { loadPdfBookmarks, PdfBookmarkItem, resolveBookmarkPage } from "./pdfBookmarks";
import { getMatchHighlightBoxes, getMatchScrollTop } from "./pdfSearchHighlight";
import {
  clampZoomPercent,
  getPercentForFitMode,
  parseZoomSelectValue,
  PDF_ZOOM_PERCENT_MAX,
  PDF_ZOOM_PERCENT_MIN,
  PdfZoomState
} from "./pdfZoom";
import {
  buildPageOffsets,
  expandPageRange,
  getPageCssDimensions,
  getPageScrollTopFromOffsets,
  getPagesInRange,
  getVisiblePageFromContainers,
  getVisiblePageFromOffsets,
  getVisiblePageRange,
  PageCssDimensions,
  shouldSyncPageFromScroll
} from "./pdfPageLayout";
import {
  getHighlightOverlayStyle,
  getHighlightPageMetrics,
  getOcrHighlightKey
} from "./pdfHighlightCoordinates";
import {
  PdfPageRenderRefs,
  prefetchPageDimensions,
  renderPdfPages
} from "./pdfPageRenderScheduler";

const getPageScrollTopInViewer = (viewer: HTMLElement, pageEl: HTMLElement) =>
  pageEl.getBoundingClientRect().top -
  viewer.getBoundingClientRect().top +
  viewer.scrollTop;

const updateControlValues = (
  payload: any,
  documentReviewDispatch: any,
  state: any
) => {
  documentReviewDispatch({
    type: ttfAction.UPDATE_PDF_CONTROLS,
    payload: {
      value: payload,
      tabToUpdate: state.activeTab
    }
  });
};

const getHighlightStartPage = (
  highlightCoordinates: any[],
  requireState = false,
  hasState = false
) => {
  if (requireState && !hasState) return 1;
  if (!highlightCoordinates.length) return 1;
  return findMinMax(highlightCoordinates, "pageNo").min;
};

const getHighlightPageNumbers = (highlightCoordinates: any[]) =>
  Array.from(
    new Set(
      highlightCoordinates
        .map((item) => Number(item?.pageNo))
        .filter((pageNo) => !Number.isNaN(pageNo) && pageNo > 0)
    )
  );

const getSearchMatchLabel = (
  searchQuery: string,
  lastSearchedQuery: string,
  searchMatches: PdfSearchMatch[],
  currentMatchIndex: number,
  isSearching: boolean
) => {
  const query = searchQuery.trim();
  if (query !== lastSearchedQuery) return "";
  if (searchMatches.length > 0 && currentMatchIndex >= 0) {
    return `${currentMatchIndex + 1} of ${searchMatches.length}`;
  }
  if (query && !isSearching) {
    return "No matches";
  }
  return "";
};

type SearchHighlightRefs = {
  pageWrapRefs: React.MutableRefObject<{ [page: number]: HTMLDivElement | null }>;
  textDivsByPageRef: React.MutableRefObject<{ [page: number]: HTMLElement[] }>;
};

const renderSearchHighlightsForPage = (
  pageNo: number,
  searchMatches: PdfSearchMatch[],
  currentMatchIndex: number,
  refs: SearchHighlightRefs,
  classes: Record<string, string>
) => {
  if (!searchMatches.length) return null;

  const pageWrap = refs.pageWrapRefs.current[pageNo];
  const textDivs = refs.textDivsByPageRef.current[pageNo];
  if (!pageWrap || !textDivs?.length) return null;

  return searchMatches.flatMap((match, globalIndex) => {
    if (match.pageNo !== pageNo) return [];
    const boxes = getMatchHighlightBoxes(pageWrap, textDivs, match);
    const isActive = globalIndex === currentMatchIndex;
    return boxes.map((box) => (
      <span
        key={`search-${pageNo}-${match.itemIndex}-${match.charStart}-${match.charEnd}-${box.left}-${box.top}-${box.width}-${box.height}`}
        className={`${classes.pdfSearchHighlight} ${
          isActive ? classes.pdfSearchHighlightActive : ""
        }`}
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height
        }}
      />
    ));
  });
};

const resolvePdfDownloadUrl = async (options: {
  url?: string;
  cacheKey?: any;
  effectiveDrawId?: string;
  overRideDrawIdCheck: boolean;
  loanId?: string;
}) => {
  const downloadURL = options.url;
  if (downloadURL || !options.cacheKey) return downloadURL || null;

  if (options.effectiveDrawId && !options.overRideDrawIdCheck) {
    const res: any = await getPresignedURLForDrawDocs(
      options.effectiveDrawId,
      Number(options.cacheKey)
    );
    return res?.data?.downloadLinks?.[0].url ?? null;
  }

  if (options.loanId) {
    const responseData: any = await getPresignedURL("LOAN", options.loanId, options.cacheKey);
    return responseData?.data?.url ?? null;
  }

  return null;
};

const usePdfViewerSize = (viewerScrollRef: RefObject<HTMLDivElement | null>) => {
  const [viewerWidth, setViewerWidth] = useState(0);
  const [viewerHeight, setViewerHeight] = useState(0);

  useEffect(() => {
    const viewer = viewerScrollRef.current;
    if (!viewer) {
      return undefined;
    }

    const updateViewerSize = () => {
      setViewerWidth(viewer.clientWidth || 0);
      setViewerHeight(viewer.clientHeight || 0);
    };

    updateViewerSize();

    if (typeof ResizeObserver === "undefined") {
      globalThis.addEventListener("resize", updateViewerSize);
      return () => {
        globalThis.removeEventListener("resize", updateViewerSize);
      };
    }

    const observer = new ResizeObserver(() => {
      updateViewerSize();
    });
    observer.observe(viewer);

    return () => {
      observer.disconnect();
    };
  }, [viewerScrollRef]);

  return { viewerWidth, viewerHeight };
};

const triggerHighlightLayoutTick = (
  setHighlightLayoutTick: React.Dispatch<React.SetStateAction<number>>
) => {
  requestAnimationFrame(() => {
    setHighlightLayoutTick((tick) => tick + 1);
  });
};

const applySearchResults = (
  matches: PdfSearchMatch[],
  normalizedQuery: string,
  goToMatch: (index: number, matches?: PdfSearchMatch[]) => void,
  setLastSearchedQuery: (query: string) => void,
  setSearchMatches: (matches: PdfSearchMatch[]) => void,
  setCurrentMatchIndex: (index: number) => void
) => {
  setLastSearchedQuery(normalizedQuery);
  setSearchMatches(matches);
  if (matches.length) {
    goToMatch(0, matches);
  } else {
    setCurrentMatchIndex(-1);
  }
};

const loadPdfDocumentFromUrl = async (
  PDFData: string,
  options: {
    highlightCoordinates: any[];
    state?: any;
    documentReviewDispatch: any;
    isCancelled: () => boolean;
    onLoaded: (doc: any, nextPage: number) => void;
    onError: () => void;
  }
) => {
  const pdfVersion =
    (pdfjsLib as unknown as { version?: string }).version || PDFJS_DIST_FALLBACK_VERSION;
  const task = pdfjsLib.getDocument(getPdfDocumentLoadParamsForUrl(PDFData, pdfVersion));
  const doc = await task.promise;
  if (options.isCancelled()) return;
  const nextPage = getHighlightStartPage(
    options.highlightCoordinates,
    true,
    Boolean(options.state)
  );
  options.onLoaded(doc, nextPage);
  if (options.state) {
    updateControlValues(
      {
        totalPages: doc.numPages,
        activePageNumber: nextPage,
        isDocumentLoading: false
      },
      options.documentReviewDispatch,
      options.state
    );
  }
};

type SearchMatchScrollRefs = {
  viewerScrollRef: RefObject<HTMLDivElement | null>;
  pageContainerRefs: RefObject<{ [page: number]: HTMLDivElement | null }>;
  pageWrapRefs: RefObject<{ [page: number]: HTMLDivElement | null }>;
  textDivsByPageRef: RefObject<{ [page: number]: HTMLElement[] }>;
};

const useSearchMatchScroll = (
  currentMatchIndex: number,
  currentPage: number,
  searchMatches: PdfSearchMatch[],
  highlightLayoutTick: number,
  refs: SearchMatchScrollRefs
) => {
  useEffect(() => {
    if (currentMatchIndex < 0 || !searchMatches.length) return;
    const match = searchMatches[currentMatchIndex];
    if (match.pageNo !== currentPage) return;
    const viewer = refs.viewerScrollRef.current;
    const pageEl = refs.pageContainerRefs.current?.[match.pageNo];
    const pageWrap = refs.pageWrapRefs.current?.[match.pageNo];
    const textDivs = refs.textDivsByPageRef.current?.[match.pageNo];
    if (!viewer || !pageEl || !pageWrap || !textDivs?.length) return;

    const boxes = getMatchHighlightBoxes(pageWrap, textDivs, match);
    const scrollTop = getMatchScrollTop(pageEl, boxes);
    if (scrollTop == null) return;

    viewer.scrollTo({
      left: 0,
      top: scrollTop,
      behavior: "auto"
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatchIndex, currentPage, searchMatches, highlightLayoutTick]);
};

const executeDocumentSearch = async (
  pdfDoc: any,
  searchQuery: string,
  totalPages: number,
  options: { matchCase: boolean; wholeWord: boolean },
  handlers: {
    onClear: () => void;
    onResults: (matches: PdfSearchMatch[], normalizedQuery: string) => void;
    onSearchingChange: (isSearching: boolean) => void;
    onLayoutTick: () => void;
  }
) => {
  if (!pdfDoc || !searchQuery.trim()) {
    handlers.onClear();
    return;
  }
  handlers.onSearchingChange(true);
  try {
    const normalizedQuery = searchQuery.trim();
    const matches = await findTextInDocument(pdfDoc, normalizedQuery, totalPages, options);
    handlers.onResults(matches, normalizedQuery);
    handlers.onLayoutTick();
  } finally {
    handlers.onSearchingChange(false);
  }
};

const PdfViewerDownloadState = ({
  downloadProgress,
  onRedownload,
  classes
}: {
  downloadProgress: any;
  onRedownload: () => void;
  classes: Record<string, string>;
}) => (
  <Grid className={classes.pdfContainer} style={{ alignItems: "center" }}>
    <InfoOnlyTooltip arrow placement="top" title="Re-download">
      <IconButton
        aria-label="Re-download"
        component="button"
        onClick={onRedownload}
        style={{ position: "absolute", right: 0, zIndex: 1 }}
        size="large"
      >
        <RefreshIcon color="#808080" width={16} height={16} />
      </IconButton>
    </InfoOnlyTooltip>
    <CircularProgressWithLabel value={downloadProgress} />
  </Grid>
);

type PDFViewerPageListProps = {
  totalPages: number;
  loadingPage: boolean;
  showHighlights: boolean;
  highlightCoordinates: any[];
  currentAngle: number;
  pixelPadding: number;
  searchMatches: PdfSearchMatch[];
  currentMatchIndex: number;
  classes: Record<string, string>;
  pageDimensions: Record<number, PageCssDimensions>;
  fallbackPageHeight: number;
  renderedPages: Record<number, boolean>;
  pageCanvasRefs: React.MutableRefObject<{ [page: number]: HTMLCanvasElement | null }>;
  pageContainerRefs: React.MutableRefObject<{ [page: number]: HTMLDivElement | null }>;
  pageWrapRefs: React.MutableRefObject<{ [page: number]: HTMLDivElement | null }>;
  pageTextLayerRefs: React.MutableRefObject<{ [page: number]: HTMLDivElement | null }>;
  textDivsByPageRef: React.MutableRefObject<{ [page: number]: HTMLElement[] }>;
  highlightLayoutTick: number;
};

const PDFViewerPageList = ({
  totalPages,
  loadingPage,
  showHighlights,
  highlightCoordinates,
  currentAngle,
  pixelPadding,
  searchMatches,
  currentMatchIndex,
  classes,
  pageDimensions,
  fallbackPageHeight,
  renderedPages,
  pageCanvasRefs,
  pageContainerRefs,
  pageWrapRefs,
  pageTextLayerRefs,
  textDivsByPageRef,
  highlightLayoutTick
}: PDFViewerPageListProps) => (
  <>
    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNo) => {
      const canvas = pageCanvasRefs.current[pageNo];
      const pageSize = pageDimensions[pageNo];
      const placeholderHeight = pageSize?.height ?? fallbackPageHeight;
      const highlightMetrics = getHighlightPageMetrics(
        pageWrapRefs.current[pageNo],
        canvas,
        pageSize
      );
      const highlightsForPage = showHighlights
        ? highlightCoordinates.filter((item: any) => Number(item?.pageNo) === Number(pageNo))
        : [];
      return (
        <div
          key={`pdf-page-${pageNo}`}
          ref={(el) => {
            pageContainerRefs.current[pageNo] = el;
          }}
          className={classes.pageOutline}
          style={{
            borderBottom: "1px solid rgb(160 160 253 / 41%)",
            position: "relative",
            minHeight: placeholderHeight > 0 ? placeholderHeight : undefined
          }}
        >
          <div
            ref={(el) => {
              pageWrapRefs.current[pageNo] = el;
            }}
            className={classes.pageCanvasWrap}
            data-highlight-layout={highlightLayoutTick}
            style={{
              minHeight: placeholderHeight > 0 ? placeholderHeight : undefined,
              backgroundColor: renderedPages[pageNo] ? undefined : "rgb(255, 255, 255)"
            }}
          >
            <canvas
              ref={(el) => {
                pageCanvasRefs.current[pageNo] = el;
              }}
            />
            <div
              ref={(el) => {
                pageTextLayerRefs.current[pageNo] = el;
              }}
              className={`textLayer ${classes.pdfTextLayerHost}`}
            />
            {renderSearchHighlightsForPage(
              pageNo,
              searchMatches,
              currentMatchIndex,
              { pageWrapRefs, textDivsByPageRef },
              classes
            )}
            {highlightsForPage.map((item: any) => (
              <span
                key={getOcrHighlightKey(pageNo, item)}
                style={getHighlightOverlayStyle(
                  item,
                  highlightMetrics,
                  currentAngle,
                  pixelPadding
                )}
              />
            ))}
          </div>
        </div>
      );
    })}
    {loadingPage && (
      <div style={{ textAlign: "center", color: "#666" }}>Loading page...</div>
    )}
  </>
);

interface PDFViewerProps {
  state?: any;
  documentReviewDispatch: any;
  cacheKey?: any;
  useCache?: boolean;
  url?: string;
  name: string;
  highlightChecked?: boolean;
  highlightDisabled?: boolean;
  onChangeHighlightSelect?: () => void;
  drawId?: string;
  overRideDrawIdCheck?: boolean;
}

export const PDFViewer = (props: PDFViewerProps) => {
  useEffect(() => {
    configurePdfJsWorker(pdfjsLib);
  }, []);

  const cacheProvider = TTFReviewCacheProvider();
  const pixelPadding = 7;
  const [PDFData, setPDFData] = useState<null | string>(null);
  const [currentAngle, setCurrentAngle] = useState<number>(0);
  const [downloadProgress, setDownloadProgress] = useState<any>(null);
  const [currentRequest, setCurrentRequest] = useState<AbortController | null>(null);
  const [redownload, setRedownload] = useState<boolean>(false);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pdfZoom, setPdfZoom] = useState<PdfZoomState>({ mode: "fit-width", percent: 100 });
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeSidebarPanel, setActiveSidebarPanel] = useState<PdfSidebarPanel>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMatchCase, setSearchMatchCase] = useState(false);
  const [searchWholeWord, setSearchWholeWord] = useState(false);
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const [searchMatches, setSearchMatches] = useState<PdfSearchMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [bookmarks, setBookmarks] = useState<PdfBookmarkItem[]>([]);
  const [copyMessage, setCopyMessage] = useState("");
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerScrollRef = useRef<HTMLDivElement | null>(null);
  const { viewerWidth, viewerHeight } = usePdfViewerSize(viewerScrollRef);
  const pageCanvasRefs = useRef<{ [page: number]: HTMLCanvasElement | null }>({});
  const pageContainerRefs = useRef<{ [page: number]: HTMLDivElement | null }>({});
  const pageTextLayerRefs = useRef<{ [page: number]: HTMLDivElement | null }>({});
  const pageWrapRefs = useRef<{ [page: number]: HTMLDivElement | null }>({});
  const textLayerTaskRefs = useRef<{ [page: number]: { cancel?: () => void } | null }>({});
  const textContentByPageRef = useRef<{ [page: number]: any[] }>({});
  const textDivsByPageRef = useRef<{ [page: number]: HTMLElement[] }>({});
  const [highlightLayoutTick, setHighlightLayoutTick] = useState(0);
  const [pageDimensions, setPageDimensions] = useState<Record<number, PageCssDimensions>>({});
  const [renderedPages, setRenderedPages] = useState<Record<number, boolean>>({});
  const [fallbackPageHeight, setFallbackPageHeight] = useState(0);
  const renderTaskRef = useRef<any>(null);
  const shouldAutoScrollToPageRef = useRef<boolean>(false);
  const pageOffsetsRef = useRef<number[]>([]);
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const optionalContentConfigRef = useRef<Promise<any> | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const renderRequestIdRef = useRef(0);
  const classes = useStyles();
  const {
    state,
    documentReviewDispatch,
    cacheKey,
    url,
    useCache = true,
    name,
    highlightChecked,
    highlightDisabled,
    drawId: drawIdFromProps,
    overRideDrawIdCheck = false,
    onChangeHighlightSelect
  } = props;
  const { loanId, loanType, loanStage, drawId } = useParams<URLParamsType>();
  const effectiveDrawId = drawIdFromProps || drawId;
  const activeTabState = state ? state[state.activeTab] : {};
  const { activePageNumber, highlightCoordinates = [] } = activeTabState || {};
  const showHighlights = (highlightCoordinates?.length || 0) > 0;
  const setPageFromControl = (pageNo: number, pageCount?: number) => {
    const maxPage = pageCount ?? totalPages;
    if (maxPage < 1) return;
    const nextPage = Math.max(1, Math.min(maxPage, pageNo));
    shouldAutoScrollToPageRef.current = true;
    setCurrentPage(nextPage);
  };

  const onClickOpenInNewTab = () => {
    window.open(
      `${
        getConfig().redirectUrl
      }internal/loan/createLoan/${loanType}/${loanStage}/${loanId}/documentViewer/${cacheKey}/${name}`
    );
  };

  const onClickDownload = (downloadUrl: string) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", name); // any other extension
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleRotate = () => {
    setCurrentAngle((prevAngle: number) => (prevAngle + 90) % 360);
  };

  const downloadFromServer = async () => {
    const ourRequest = new AbortController();
    setCurrentRequest(ourRequest);
    const downloadURL = await resolvePdfDownloadUrl({
      url,
      cacheKey,
      effectiveDrawId,
      overRideDrawIdCheck,
      loanId
    });
    if (!downloadURL) return null;

    const response = await axios({
      url: downloadURL,
      signal: ourRequest.signal,
      responseType: "blob",
      onDownloadProgress(progressEvent: any) {
        const progress = Math.round(
          (progressEvent?.loaded / progressEvent?.total) * 100
        );
        setDownloadProgress(progress);
      }
    });
    const { data } = response;
    if (cacheKey) {
      cacheProvider.setItem(cacheKey, data);
    }
    return data;
  };

  const readFile = async (preferCache = false) => {
    try {
      const data =
        (preferCache && cacheKey && (await cacheProvider.getItem(cacheKey))) ||
        (await downloadFromServer());
      if (url) {
        setPDFData(url);
      } else {
        setPDFData(globalThis.URL.createObjectURL(data));
      }
    } catch {
      console.info(`Error reading data from cache`);
    }
  };

  const clearSearchState = () => {
    setSearchQuery("");
    setLastSearchedQuery("");
    setSearchMatches([]);
    setCurrentMatchIndex(-1);
  };

  const handleClearSearch = () => {
    clearSearchState();
  };

  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    if (value.trim() !== lastSearchedQuery) {
      setSearchMatches([]);
      setCurrentMatchIndex(-1);
    }
  };

  const triggerRender = () => {
    clearSearchState();
    if (currentRequest !== null) {
      currentRequest.abort();
      setCurrentRequest(null);
    }
    setPDFData(null);
    const payload = {
      isDocumentLoading: true
    };
    if (state) {
      updateControlValues(payload, documentReviewDispatch, state);
    }
    setDownloadProgress(null);
    readFile(useCache);
  };

  useEffect(() => {
    setCurrentAngle(0);
  }, [cacheKey]);

  useEffect(() => {
    triggerRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, redownload, url]);

  useEffect(() => {
    if (!PDFData) {
      return undefined;
    }
    let cancelled = false;
    loadPdfDocumentFromUrl(PDFData, {
      highlightCoordinates,
      state,
      documentReviewDispatch,
      isCancelled: () => cancelled,
      onLoaded: (doc, nextPage) => {
        setPdfDoc(doc);
        setTotalPages(doc.numPages || 0);
        shouldAutoScrollToPageRef.current = true;
        setPageFromControl(nextPage, doc.numPages);
      },
      onError: () => {
        setPdfDoc(null);
        setTotalPages(0);
      }
    }).catch(() => {
      setPdfDoc(null);
      setTotalPages(0);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PDFData]);

  useEffect(() => {
    if (!pdfDoc) {
      setBookmarks([]);
      return;
    }
    loadPdfBookmarks(pdfDoc).then(setBookmarks);
    textContentByPageRef.current = {};
    textDivsByPageRef.current = {};
    renderedPagesRef.current = new Set();
    setRenderedPages({});
    setPageDimensions({});
    setFallbackPageHeight(0);
    optionalContentConfigRef.current = pdfDoc.getOptionalContentConfig({ intent: "any" });
    setHighlightLayoutTick(0);
  }, [pdfDoc]);

  useEffect(() => {
    if (!state || !activePageNumber) return;
    shouldAutoScrollToPageRef.current = true;
    setCurrentPage(activePageNumber);
  }, [activePageNumber]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === pdfContainerRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    pageOffsetsRef.current = buildPageOffsets(totalPages, pageDimensions, fallbackPageHeight);
  }, [totalPages, pageDimensions, fallbackPageHeight]);

  const getPageRenderRefs = (): PdfPageRenderRefs => ({
    viewerScrollRef,
    pageCanvasRefs,
    pageTextLayerRefs,
    pageWrapRefs,
    textLayerTaskRefs,
    textContentByPageRef,
    textDivsByPageRef,
    renderTaskRef
  });

  const cancelActivePageRenders = () => {
    renderRequestIdRef.current += 1;
    if (renderTaskRef.current?.cancel) {
      renderTaskRef.current.cancel();
    }
    Object.values(textLayerTaskRefs.current).forEach((task) => task?.cancel?.());
    textLayerTaskRefs.current = {};
  };

  const queuePageRenders = (pageNumbers: number[], priorityPage?: number) => {
    if (!pdfDoc || !totalPages || !pageNumbers.length) return;

    const pagesToRender = pageNumbers.filter((pageNo) => !renderedPagesRef.current.has(pageNo));
    if (!pagesToRender.length) {
      setLoadingPage(false);
      return;
    }

    cancelActivePageRenders();
    const requestId = renderRequestIdRef.current;
    setLoadingPage(true);
    const orderedPages = getPagesInRange(
      Math.min(...pagesToRender),
      Math.max(...pagesToRender),
      priorityPage
    ).filter((pageNo) => pagesToRender.includes(pageNo));

    renderPdfPages({
      pdfDoc,
      pageNumbers: orderedPages,
      pdfZoom,
      currentAngle,
      viewerWidth,
      viewerHeight,
      refs: getPageRenderRefs(),
      isCancelled: () => requestId !== renderRequestIdRef.current,
      optionalContentConfigPromise: optionalContentConfigRef.current || undefined,
      onPageRendered: (pageNo) => {
        if (requestId !== renderRequestIdRef.current) return;
        renderedPagesRef.current.add(pageNo);
        setRenderedPages((prev) => ({ ...prev, [pageNo]: true }));
        setHighlightLayoutTick((tick) => tick + 1);
      }
    })
      .catch(() => undefined)
      .finally(() => {
        if (requestId === renderRequestIdRef.current) {
          setLoadingPage(false);
        }
      });
  };

  const queueVisiblePageRenders = (priorityPage = currentPage) => {
    const viewer = viewerScrollRef.current;
    if (!viewer || totalPages < 1) return;

    const range = getVisiblePageRange(
      viewer.scrollTop,
      viewer.clientHeight,
      pageOffsetsRef.current,
      totalPages
    );
    const expanded = expandPageRange(range.first, range.last, priorityPage, totalPages);
    const visiblePages = Array.from(
      { length: expanded.last - expanded.first + 1 },
      (_, index) => expanded.first + index
    );
    const highlightPages = showHighlights ? getHighlightPageNumbers(highlightCoordinates) : [];
    const pagesToQueue = Array.from(new Set([...visiblePages, ...highlightPages]));
    queuePageRenders(pagesToQueue, priorityPage);
  };

  const fitPageViewerHeight = pdfZoom.mode === "fit-page" ? viewerHeight : 0;

  useEffect(() => {
    if (!pdfDoc || !totalPages) {
      return undefined;
    }
    let cancelled = false;
    const viewerEl = viewerScrollRef.current;
    const savedScrollTop = viewerEl?.scrollTop ?? 0;

    cancelActivePageRenders();
    renderedPagesRef.current = new Set();
    setRenderedPages({});
    textContentByPageRef.current = {};
    textDivsByPageRef.current = {};
    optionalContentConfigRef.current = pdfDoc.getOptionalContentConfig({ intent: "any" });

    const seedDimensions = async () => {
      try {
        const firstPage = await pdfDoc.getPage(1);
        if (cancelled) return;
        const firstDimensions = getPageCssDimensions(
          firstPage,
          pdfZoom,
          viewerWidth,
          viewerHeight,
          currentAngle
        );
        setFallbackPageHeight(firstDimensions.height);
        setPageDimensions({ 1: firstDimensions });
      } catch {
        // Fall back to lazy dimension discovery during render.
      }
    };

    seedDimensions().finally(() => {
      if (!cancelled) {
        queueVisiblePageRenders(currentPage);
      }
    });

    prefetchPageDimensions({
      pdfDoc,
      totalPages,
      pdfZoom,
      currentAngle,
      viewerWidth,
      viewerHeight,
      isCancelled: () => cancelled,
      onBatch: (dimensions) => {
        if (!cancelled) {
          setPageDimensions(dimensions);
        }
      }
    });

    return () => {
      cancelled = true;
      cancelActivePageRenders();
      if (viewerEl && !shouldAutoScrollToPageRef.current) {
        viewerEl.scrollTop = savedScrollTop;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pdfDoc,
    totalPages,
    pdfZoom,
    currentAngle,
    viewerWidth,
    fitPageViewerHeight
  ]);

  useEffect(() => {
    if (!currentPage) return;
    queueVisiblePageRenders(currentPage);
    if (!shouldAutoScrollToPageRef.current) return;
    const viewer = viewerScrollRef.current;
    if (!viewer) return;

    const container = pageContainerRefs.current[currentPage];
    let offsetTop = 0;
    if (container) {
      offsetTop = getPageScrollTopInViewer(viewer, container);
    } else if (pageOffsetsRef.current.length) {
      offsetTop = getPageScrollTopFromOffsets(currentPage, pageOffsetsRef.current);
    }

    viewer.scrollTo({
      left: 0,
      top: offsetTop,
      behavior: "auto"
    });
    shouldAutoScrollToPageRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageDimensions, fallbackPageHeight]);

  const onDocumentLoadSuccess = () => {
    const payload = {
      totalPages,
      activePageNumber: getHighlightStartPage(highlightCoordinates),
      isDocumentLoading: false
    };
    if (state) {
      updateControlValues(payload, documentReviewDispatch, state);
    }
  };

  useEffect(() => {
    onDocumentLoadSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const reDownloadDoc = () => {
    setRedownload((prevState) => !prevState);
  };

  const goToMatch = (index: number, matches: PdfSearchMatch[] = searchMatches) => {
    if (!matches.length) return;
    const safeIndex = ((index % matches.length) + matches.length) % matches.length;
    setCurrentMatchIndex(safeIndex);
    setPageFromControl(matches[safeIndex].pageNo);
  };

  const runSearch = () =>
    executeDocumentSearch(
      pdfDoc,
      searchQuery,
      totalPages,
      { matchCase: searchMatchCase, wholeWord: searchWholeWord },
      {
        onClear: () => {
          setSearchMatches([]);
          setCurrentMatchIndex(-1);
        },
        onResults: (matches, normalizedQuery) =>
          applySearchResults(
            matches,
            normalizedQuery,
            goToMatch,
            setLastSearchedQuery,
            setSearchMatches,
            setCurrentMatchIndex
          ),
        onSearchingChange: setIsSearching,
        onLayoutTick: () => triggerHighlightLayoutTick(setHighlightLayoutTick)
      }
    );

  useEffect(() => {
    if (!pdfDoc || !lastSearchedQuery) return;
    void runSearch();
    // Re-run when search options change; runSearch uses current query and flags.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMatchCase, searchWholeWord]);

  const handleSearchPrev = () => {
    if (!searchMatches.length || currentMatchIndex < 0) return;
    goToMatch(currentMatchIndex - 1);
  };

  const handleSearchNext = () => {
    if (!searchMatches.length || currentMatchIndex < 0) return;
    goToMatch(currentMatchIndex + 1);
  };

  const handleCopySelection = async () => {
    const selectedText = globalThis.getSelection()?.toString()?.trim();
    if (!selectedText) {
      setCopyMessage("Select text on the PDF first");
      setTimeout(() => setCopyMessage(""), 2500);
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedText);
      setCopyMessage("Copied to clipboard");
    } catch {
      setCopyMessage("Unable to copy selection");
    }
    setTimeout(() => setCopyMessage(""), 2500);
  };

  const handleBookmarkClick = async (bookmark: PdfBookmarkItem) => {
    if (!pdfDoc) return;
    const pageNo = await resolveBookmarkPage(pdfDoc, bookmark.dest);
    if (!pageNo) return;
    setPageFromControl(pageNo);
  };

  const handleThumbnailPageSelect = (pageNo: number) => {
    setPageFromControl(pageNo);
  };

  const toggleFullscreen = async () => {
    const container = pdfContainerRef.current;
    if (!container) return;
    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
      } else {
        await container.requestFullscreen();
      }
    } catch {
      // Fullscreen may be blocked by browser policy.
    }
  };

  const handleRotateCounterClockwise = () => {
    setCurrentAngle((prevAngle: number) => (prevAngle + 270) % 360);
  };

  const bumpZoomPercent = (delta: number) => {
    shouldAutoScrollToPageRef.current = true;
    setPdfZoom((prev) => {
      const basePercent =
        prev.mode === "percent"
          ? prev.percent
          : getPercentForFitMode(prev.mode, 1, 1);
      return {
        mode: "percent",
        percent: clampZoomPercent(basePercent + delta)
      };
    });
  };

  const handleZoomSelectChange = (value: string) => {
    shouldAutoScrollToPageRef.current = true;
    setPdfZoom(parseZoomSelectValue(value));
  };

  const isZoomPercentMin = pdfZoom.mode === "percent" && pdfZoom.percent <= PDF_ZOOM_PERCENT_MIN;
  const isZoomPercentMax = pdfZoom.mode === "percent" && pdfZoom.percent >= PDF_ZOOM_PERCENT_MAX;

  const searchMatchLabel = getSearchMatchLabel(
    searchQuery,
    lastSearchedQuery,
    searchMatches,
    currentMatchIndex,
    isSearching
  );
  const hasCurrentSearchResults =
    searchMatches.length > 0 && lastSearchedQuery === searchQuery.trim();

  useSearchMatchScroll(currentMatchIndex, currentPage, searchMatches, highlightLayoutTick, {
    viewerScrollRef,
    pageContainerRefs,
    pageWrapRefs,
    textDivsByPageRef
  });

  if (!PDFData) {
    return (
      <PdfViewerDownloadState
        downloadProgress={downloadProgress}
        onRedownload={reDownloadDoc}
        classes={classes}
      />
    );
  }
  const isFirstPage = currentPage <= 1;
  const isLastPage = totalPages === 0 || currentPage >= totalPages;
  const handleViewerScroll = () => {
    if (scrollRafRef.current != null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const viewer = viewerScrollRef.current;
      if (!viewer || totalPages < 1) return;

      queueVisiblePageRenders();

      if (!shouldSyncPageFromScroll(shouldAutoScrollToPageRef.current, totalPages)) return;
      const visiblePage =
        getVisiblePageFromContainers(
          viewer.scrollTop,
          (pageNo: number) => {
            const container = pageContainerRefs.current[pageNo];
            if (!container) return null;
            return getPageScrollTopInViewer(viewer, container);
          },
          totalPages
        ) ??
        getVisiblePageFromOffsets(
          viewer.scrollTop,
          pageOffsetsRef.current,
          totalPages
        );
      if (visiblePage !== currentPage) {
        setCurrentPage(visiblePage);
      }
    });
  };

  return (
    <Grid
      ref={pdfContainerRef}
      className={`${classes.pdfContainer} ${isFullscreen ? classes.pdfContainerFullscreen : ""}`}
      id="pdfContainer"
      data-testid="pdfContainer"
    >
      <PDFViewerToolbar
        classes={classes}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        currentPage={currentPage}
        totalPages={totalPages}
        pdfZoom={pdfZoom}
        isZoomPercentMin={isZoomPercentMin}
        isZoomPercentMax={isZoomPercentMax}
        isFullscreen={isFullscreen}
        highlightChecked={highlightChecked}
        highlightDisabled={highlightDisabled}
        useCache={useCache}
        pdfData={PDFData}
        onSetPageFromControl={setPageFromControl}
        onBumpZoomPercent={bumpZoomPercent}
        onZoomSelectChange={handleZoomSelectChange}
        onToggleFullscreen={toggleFullscreen}
        onRotateCounterClockwise={handleRotateCounterClockwise}
        onRotate={handleRotate}
        onOpenInNewTab={onClickOpenInNewTab}
        onDownload={onClickDownload}
        onChangeHighlightSelect={onChangeHighlightSelect}
      />

      <div
        className={`${classes.pdfViewerBody} ${
          isFullscreen ? classes.pdfViewerBodyFullscreen : ""
        }`}
      >
        <PDFViewerSidebar
          classes={classes}
          isFullscreen={isFullscreen}
          activePanel={activeSidebarPanel}
          onPanelChange={setActiveSidebarPanel}
          searchQuery={searchQuery}
          onSearchQueryChange={handleSearchQueryChange}
          onClearSearch={handleClearSearch}
          searchMatchCase={searchMatchCase}
          searchWholeWord={searchWholeWord}
          onSearchMatchCaseChange={setSearchMatchCase}
          onSearchWholeWordChange={setSearchWholeWord}
          onSearch={runSearch}
          onSearchPrev={handleSearchPrev}
          onSearchNext={handleSearchNext}
          searchMatchLabel={searchMatchLabel}
          isSearching={isSearching}
          onCopySelection={handleCopySelection}
          copyMessage={copyMessage}
          bookmarks={bookmarks}
          onBookmarkClick={handleBookmarkClick}
          hasSearchResults={hasCurrentSearchResults}
          onSearchEnterKey={() => {
            if (hasCurrentSearchResults) handleSearchNext();
            else runSearch();
          }}
          onSearchShiftEnter={() => {
            if (hasCurrentSearchResults) handleSearchPrev();
          }}
          pdfDoc={pdfDoc}
          totalPages={totalPages}
          currentPage={currentPage}
          onThumbnailPageSelect={handleThumbnailPageSelect}
        />
        <div
          ref={viewerScrollRef}
          className={isFullscreen ? classes.pdfViewerScrollFullscreen : undefined}
          style={{
            position: "relative",
            overflow: "auto",
            width: "100%",
            height: "100%",
            paddingTop: 8,
            minWidth: 0
          }}
          onScroll={handleViewerScroll}
        >
        <div
          className={`${classes.pdfViewerPagesColumn} ${
            isFullscreen ? classes.pdfViewerPagesColumnFullscreen : ""
          }`}
        >
          <PDFViewerPageList
            totalPages={totalPages}
            loadingPage={loadingPage}
            showHighlights={showHighlights}
            highlightCoordinates={highlightCoordinates}
            currentAngle={currentAngle}
            pixelPadding={pixelPadding}
            searchMatches={searchMatches}
            currentMatchIndex={currentMatchIndex}
            classes={classes}
            pageDimensions={pageDimensions}
            fallbackPageHeight={fallbackPageHeight}
            renderedPages={renderedPages}
            pageCanvasRefs={pageCanvasRefs}
            pageContainerRefs={pageContainerRefs}
            pageWrapRefs={pageWrapRefs}
            pageTextLayerRefs={pageTextLayerRefs}
            textDivsByPageRef={textDivsByPageRef}
            highlightLayoutTick={highlightLayoutTick}
          />
        </div>
        </div>
      </div>
    </Grid>
  );
};